import React, { useEffect, useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import type { ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useResolveAgentAri } from '@atlassian/conversation-assistant-agent/controllers/agent-ari';
import { RovoAgentConversationStarters } from '@atlassian/rovo-conversation-actions/rovo-agent-conversation-starters';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useCurrentChatConversation } from '../../../controllers/conversation/useCurrentChatConversation';

import type {
	AgentStudioAgentVersionType,
	RovoAgentConversationStartersNewQuery,
	RovoAgentConversationStartersNewQuery$variables,
} from './__generated__/RovoAgentConversationStartersNewQuery.graphql';
import rovoAgentConversationStartersNewQuery from './__generated__/RovoAgentConversationStartersNewQuery.graphql';

type RovoAgentConversationStartersNewProps = {
	agentId: string;
	agentAri?: string | null;
	onConversationStarterClick: (conversationStarter: ConversationStarter) => void;
};

const styles = cssMap({
	skeletonRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		minHeight: '44px',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

const ConversationStartersSkeleton = (): React.JSX.Element => (
	<Stack space="space.050" testId="rovo-agent-conversation-starters-skeleton">
		{['55%', '70%', '65%'].map((width) => (
			<Box key={width} xcss={styles.skeletonRow}>
				<Skeleton width={32} height={32} borderRadius={token('radius.large')} isShimmering />
				<Skeleton width={width} height={14} borderRadius={token('radius.small')} isShimmering />
			</Box>
		))}
	</Stack>
);

const getVersionVariables = ({
	agentAri,
	agentVersion,
}: {
	agentAri: string;
	agentVersion: ReturnType<typeof useCurrentChatConversation>['agentVersion'];
}): RovoAgentConversationStartersNewQuery$variables => ({
	agentAri,
	versionId: agentVersion?.versionId ?? null,
	versionType: agentVersion?.versionId
		? null
		: ((agentVersion?.versionType ?? 'LIVE') as AgentStudioAgentVersionType),
});

const getStateCacheKey = ({
	agentAri,
	versionId,
	versionType,
}: RovoAgentConversationStartersNewQuery$variables): string =>
	`${agentAri}:${versionId ?? versionType ?? 'LIVE'}`;

const getVersionMatchingRelayAgent = ({
	relayAgent,
	versionId,
	versionType,
}: {
	relayAgent: RovoAgentConversationStartersNewQuery['response']['agentStudio_agentById'];
	versionId: RovoAgentConversationStartersNewQuery$variables['versionId'];
	versionType: RovoAgentConversationStartersNewQuery$variables['versionType'];
}) => {
	if (relayAgent?.__typename !== 'AgentStudioAssistant') {
		return null;
	}

	if (versionId) {
		return relayAgent.version?.id === versionId ? relayAgent : null;
	}

	return relayAgent.version?.type === versionType ? relayAgent : null;
};

/**
 * Keeps Chat conversation starters aligned with its selected Agent version:
 * - Reuses Relay data only when its version matches Chat
 * - Fetches mismatches without normalization and caches raw strings by Agent/version
 * - Shows a skeleton while the selected version loads, avoiding starters from another version
 *
 * Agent versions share one Relay record ID, so normalizing a mismatch would overwrite Studio data.
 * Remove this workaround when RAGE-4123 is resolved:
 * https://product-fabric.atlassian.net/browse/RAGE-4123
 */
export const RovoAgentConversationStartersNew = ({
	agentId,
	agentAri: existingAgentAri,
	onConversationStarterClick,
}: RovoAgentConversationStartersNewProps): React.JSX.Element => {
	di(useResolveAgentAri, useGlobalConfig, useCurrentChatConversation);
	const environment = useRelayEnvironment();
	const { siteId } = useGlobalConfig();
	const { agentVersion } = useCurrentChatConversation();
	const { agentAri } = useResolveAgentAri({
		agentId,
		existingAgentAri,
		cloudId: siteId,
	});

	const variables = useMemo(
		() => getVersionVariables({ agentAri: agentAri ?? '', agentVersion }),
		[agentAri, agentVersion],
	);
	const stateCacheKey = getStateCacheKey(variables);
	const relayCache = useLazyLoadQuery<RovoAgentConversationStartersNewQuery>(
		graphql`
			query RovoAgentConversationStartersNewQuery(
				$agentAri: ID!
				$versionType: AgentStudioAgentVersionType
				$versionId: ID
			) {
				agentStudio_agentById(
					id: $agentAri
					version: { versionType: $versionType, versionId: $versionId }
				) @optIn(to: "AgentStudio") {
					__typename
					... on AgentStudioAssistant {
						conversationStarters
						version {
							id
							type
						}
					}
				}
			}
		`,
		variables,
		{
			fetchPolicy: 'store-only',
		},
	);
	const [stateCache, setStateCache] = useState<Readonly<Record<string, ReadonlyArray<string>>>>({});

	const matchingRelayCacheAgent = useMemo(
		() =>
			getVersionMatchingRelayAgent({
				relayAgent: relayCache?.agentStudio_agentById,
				versionId: variables.versionId,
				versionType: variables.versionType,
			}),
		[relayCache?.agentStudio_agentById, variables.versionId, variables.versionType],
	);
	const stateCachedConversationStarters = stateCache[stateCacheKey];

	useEffect(() => {
		if (!matchingRelayCacheAgent) {
			return;
		}

		setStateCache((current) => ({
			...current,
			[stateCacheKey]: matchingRelayCacheAgent.conversationStarters ?? [],
		}));
	}, [matchingRelayCacheAgent, stateCacheKey]);

	useEffect(() => {
		if (!agentAri || matchingRelayCacheAgent || stateCachedConversationStarters !== undefined) {
			return;
		}

		// Calling the network layer directly returns the raw response without Relay normalization,
		// so this version cannot overwrite the shared Agent record in the Relay cache.
		const subscription = environment
			.getNetwork()
			.execute(rovoAgentConversationStartersNewQuery.params, variables, { force: true })
			.subscribe({
				next: (response) => {
					if (!('data' in response)) {
						return;
					}

					const data = response.data as
						| RovoAgentConversationStartersNewQuery['response']
						| null
						| undefined;
					if (data?.agentStudio_agentById?.__typename !== 'AgentStudioAssistant') {
						return;
					}
					const conversationStarters = data.agentStudio_agentById.conversationStarters ?? [];

					setStateCache((current) => ({
						...current,
						[stateCacheKey]: conversationStarters,
					}));
				},
			});

		return () => subscription.unsubscribe();
	}, [
		agentAri,
		environment,
		stateCachedConversationStarters,
		stateCacheKey,
		variables,
		matchingRelayCacheAgent,
	]);

	const getConversationStarters = (): ReadonlyArray<string> | null => {
		if (matchingRelayCacheAgent) {
			return matchingRelayCacheAgent.conversationStarters ?? [];
		}

		if (stateCachedConversationStarters !== undefined) {
			return stateCachedConversationStarters;
		}

		if (!agentAri) {
			return [];
		}

		return null;
	};

	const conversationStarters = getConversationStarters();

	if (conversationStarters === null) {
		return <ConversationStartersSkeleton />;
	}

	return (
		<RovoAgentConversationStarters
			agentId={agentId}
			conversationStarters={conversationStarters}
			onConversationStarterClick={onConversationStarterClick}
		/>
	);
};
